import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BillingNav } from './BillingNav';
import { billingApi } from '@/services/billingApi';
import { toast } from 'sonner';

export default function BillingSettingsPage() {
  const [settings, setSettings] = useState<any>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [sequences, setSequences] = useState<Array<{ kind: string; year: number; nextNumber: number }>>([]);

  const load = () => {
    billingApi.settings().then((d) => {
      setSettings(d.settings);
      setYear(d.year);
      setSequences(d.sequences);
    }).catch((e) => toast.error(e.response?.data?.message || 'Errore'));
  };

  useEffect(() => { load(); }, []);

  if (!settings) return <div>Caricamento...</div>;

  const field = (key: string, label: string) => (
    <div>
      <Label>{label}</Label>
      <Input value={settings[key] || ''} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} />
    </div>
  );

  return (
    <div className="space-y-4 max-w-3xl">
      <h1 className="text-2xl font-bold">Impostazioni preventivi e conti</h1>
      <BillingNav />
      <Card>
        <CardHeader><CardTitle className="text-base">Intestazione PDF</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          {field('legalName', 'Ragione sociale')}
          {field('address', 'Indirizzo')}
          {field('postalCode', 'CAP')}
          {field('city', 'Città')}
          {field('province', 'Provincia')}
          {field('fiscalCode', 'Codice fiscale')}
          {field('vatNumber', 'P.IVA')}
          {field('pec', 'PEC')}
          {field('sdiCode', 'Codice SDI')}
          {field('iban', 'IBAN')}
          {field('phone', 'Telefono')}
          {field('email', 'Email')}
          <div className="md:col-span-2">
            <Label>Logo</Label>
            <Input type="file" accept="image/png,image/jpeg" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const next = await billingApi.uploadLogo(file);
              setSettings(next);
              toast.success('Logo aggiornato');
            }} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Numerazione e scadenze</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          {field('quotePrefix', 'Prefisso preventivi')}
          {field('invoicePrefix', 'Prefisso fatture')}
          <div>
            <Label>Validità preventivo (giorni)</Label>
            <Input type="number" value={settings.quoteValidityDays} onChange={(e) => setSettings({ ...settings, quoteValidityDays: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Scadenza fattura (giorni)</Label>
            <Input type="number" value={settings.invoiceDueDays} onChange={(e) => setSettings({ ...settings, invoiceDueDays: Number(e.target.value) })} />
          </div>
          {sequences.map((s) => (
            <div key={s.kind}>
              <Label>Prossimo {s.kind === 'QUOTE' ? 'preventivo' : 'fattura/NC'} {year}</Label>
              <div className="flex gap-2">
                <Input defaultValue={s.nextNumber} id={`next-${s.kind}`} />
                <Button variant="outline" onClick={async () => {
                  const el = document.getElementById(`next-${s.kind}`) as HTMLInputElement;
                  await billingApi.setNextNumber({ kind: s.kind, year, nextNumber: Number(el.value) });
                  toast.success('Prossimo numero aggiornato');
                  load();
                }}>Salva n.</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Modelli messaggio</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {field('emailSubjectTemplate', 'Oggetto email')}
          <div>
            <Label>Corpo email</Label>
            <Textarea rows={4} value={settings.emailBodyTemplate} onChange={(e) => setSettings({ ...settings, emailBodyTemplate: e.target.value })} />
          </div>
          {field('whatsappCaptionTemplate', 'Didascalia WhatsApp')}
        </CardContent>
      </Card>
      <Button onClick={async () => {
        await billingApi.saveSettings(settings);
        toast.success('Impostazioni salvate');
      }}>Salva impostazioni</Button>
    </div>
  );
}
